import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import type { StepProps } from '../types'

/**
 * Step 6: their data. The import happens in their app (sign in as their
 * administrator from Customers, or leave it to them); this records that it is
 * done. LipaFlow counts what their account holds, so nobody types a number.
 */
export default function ImportDataStep({ tenantId }: StepProps) {
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState('')

  const completeMutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ count: number; unit_label: string }>('/admin/wizard/complete-import', {
        tenant_id: tenantId,
        notes: notes.trim() || undefined,
      })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success(`Recorded: ${data.count.toLocaleString('en-KE')} ${data.unit_label} in their account`)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, "Couldn't record the import"))
    },
  })

  const skipMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/skip-import', { tenant_id: tenantId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('Skipped: they will import their own data')
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, "Couldn't skip the import"))
    },
  })

  return (
    <div>
      <h2 className="text-xl font-semibold mb-1">Step 6: Their data (optional)</h2>
      <p className="text-body-sm text-ink-muted mb-4">
        If you import their records for them, do it in their app: open the customer under Customers and sign in as
        their administrator. When you're done, mark it here and LipaFlow records what their account holds.
        Otherwise skip, and they import it themselves.
      </p>
      <label className="block mb-6">
        <span className="block text-sm font-medium text-ink">Notes (optional)</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Imported from their spreadsheet; two buildings still to add"
          className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
        />
      </label>
      <div className="flex justify-between">
        <button
          onClick={() => skipMutation.mutate()}
          disabled={skipMutation.isPending}
          className="px-4 py-2 text-ink-muted hover:text-ink"
        >
          Skip: they will import it
        </button>
        <button
          onClick={() => completeMutation.mutate()}
          disabled={completeMutation.isPending}
          className="px-6 py-2 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50"
        >
          {completeMutation.isPending ? 'Recording…' : 'Mark the import done'}
        </button>
      </div>
    </div>
  )
}
